import { Checkbox, Field, Link, Text } from '@fluentui/react-components';
import { AddCircleRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { FileUploadConfiguration, questionnaireImageFileConfiguration, questionnaireVideoFileConfiguration } from '../../../configurations';
import { fileUploadValidator } from '../../../shared/functions';
import { FlexBox } from '../../shared-components/flex-box';
import { UploadButton, ValidationMessageList } from '../../shared-components';
import DocumentFile from '../../shared-components/DocumentFile';
import type { QuestionnaireMediaType } from '../questionnaire-wizard-page-ui-store';
import './UploadMediaSection.scss';

interface UploadMediaSectionProps {
  onUploadMedia(mediaContent: File): void;
  onDeleteMedia(uploadedMediaId: number): void;
}

const UploadMediaSectionImpl: React.FC<UploadMediaSectionProps> = (props) => {
  const {
    onUploadMedia,
    onDeleteMedia,
  } = props;

  const {
    questionnaireWizardPageUiStore,
  } = useStores();

  const isBeingValidated = questionnaireWizardPageUiStore.isBeingValidated('media');

  const [errorMessages, setErrorMessages] = useState<Record<QuestionnaireMediaType, string[]>>({
    image: [],
    video: [],
  });

  const fileConfigurations: Record<QuestionnaireMediaType, FileUploadConfiguration> = {
    image: questionnaireImageFileConfiguration,
    video: questionnaireVideoFileConfiguration,
  };

  const handleUploadedFile = (files: FileList | null, mediaType: QuestionnaireMediaType): void => {
    setErrorMessages(prev => ({ ...prev, [mediaType]: [] }));

    if (!files?.[0] || files.length > 1) return;

    const file = files[0];
    const result = fileUploadValidator(file, fileConfigurations[mediaType]);
    if (!result.isValid) {
      setErrorMessages(prev => ({ ...prev, [mediaType]: result.errorMessages }));
      return;
    }

    onUploadMedia(file);
  };

  const renderUploadedMedia = (mediaType: QuestionnaireMediaType): React.JSX.Element[] => (
    questionnaireWizardPageUiStore.getUploadedMedia(mediaType)
      .map(medium => (
        <DocumentFile
          key={medium.uploadedMediaId}
          filename={medium.originalFileName}
          onDeleteFile={() => onDeleteMedia(medium.uploadedMediaId)}
        />
      ))
  );

  const renderImagesRequest = (): React.JSX.Element => {
    return (
      <FlexBox
        isFullWidth
        direction='column'
        aligns='start'
        className='upload-media-section__group'
      >
        <Text weight='semibold' size={400} block>Photography request</Text>
        <ul>
          <li>We are hoping for 2-4 photos and video clips of you and your team.</li>
          <li>These do not need to be professional, mobile device images are fine.</li>
          <li>Examples can be viewed as part of our&nbsp;
            <Link
              rel='noopener noreferrer'
              target='_blank'
              href={'https://www.youtube.com/watch?v=NTgc_mbpfSQ'}
            >
              2024 celebration video
            </Link>
            <ul>
              <li>Group photos of you and your colleagues celebrating or working together.</li>
              <li>Image of you and your colleagues conducting a meeting over Teams either as a screen shot or a picture taken by your phone. Please make sure that everyone on the call is aware that you are taking the picture and how the picture will be used.</li>
              <li>You or your team doing charity work or work in your community.</li>
            </ul>
          </li>
        </ul>
        <UploadButton
          onUpload={(fileList) => handleUploadedFile(fileList, 'image')}
          acceptFiles={fileConfigurations['image'].supportedFileExtensions.join(',')}
          validationMessage={errorMessages['image'].join('\n')}
          infoLabel='(File types accepted: jpg, png, gif, tif, eps and svg)'
          buttonProps={{
            appearance: 'primary',
            children: 'Upload images',
            icon: (<AddCircleRegular />),
            disabled: questionnaireWizardPageUiStore.isMediaUploadLimitReached,
          }}
        >
          {renderUploadedMedia('image')}
        </UploadButton>
      </FlexBox>
    );
  }

  const renderVideoRequest = (): React.JSX.Element => {
    return (
      <FlexBox
        isFullWidth
        direction='column'
        aligns='start'
        className='upload-media-section__group'
      >
        <Text weight='semibold' size={400} block>Video clips</Text>
        <Text block>
          We would love to have short clips (20 seconds or less) of the following.
        </Text>
        <Text weight='semibold' block><u>Format:</u></Text>
        <ul>
          <li>Captured with a phone, horizontal if possible</li>
          <li>File type .mp4, .wmv, or .asf</li>
          <li>Max file size 20 MB</li>
          <li>Check out the clips in our&nbsp;
            <Link
              rel='noopener noreferrer'
              target='_blank'
              href={'https://www.youtube.com/watch?v=NTgc_mbpfSQ'}
            >
              2024 celebration video
            </Link> for examples
          </li>
        </ul>
        <Text weight='semibold' size={400} block>Just one more request, would you like to capture a few additional items?</Text>
        <Text weight='semibold' block>If you’re up for it, we’d also like these clips…</Text>
        <ol>
          <li>Human countdown:
            <ol type='a'>
              <li>Can we get you counting down from the number 10, end with the word…Go! </li>
            </ol>
          </li>
          <li>Celebrating:
            <ol type='a'>
              <li>Let’s see you cheering and/or clapping!</li>
            </ol>
          </li>
        </ol>
        <UploadButton
          onUpload={(fileList) => handleUploadedFile(fileList, 'video')}
          acceptFiles={fileConfigurations['video'].supportedFileExtensions.join(',')}
          validationMessage={errorMessages['video'].join('\n')}
          infoLabel='(File types accepted: mp4, wmv, asf)'
          buttonProps={{
            appearance: 'primary',
            children: 'Video Clips',
            icon: (<AddCircleRegular />),
            disabled: questionnaireWizardPageUiStore.isMediaUploadLimitReached,
          }}
        >
          {renderUploadedMedia('video')}
        </UploadButton>
      </FlexBox>
    );
  }

  return (
    <>
      <FlexBox
        isFullWidth
        direction='column'
        aligns='start'
        className='upload-media-section'
      >
        <Text weight='semibold' size={400} block>We would love some photos and video!</Text>
        {renderImagesRequest()}
        {renderVideoRequest()}
        <Field
          validationMessage={
            isBeingValidated && questionnaireWizardPageUiStore.questionnaireMediaValidation.fieldErrors.mediaConsentDisclaimer?.length
              ? (
                  <ValidationMessageList
                    messages={questionnaireWizardPageUiStore.questionnaireMediaValidation.fieldErrors.mediaConsentDisclaimer}
                  />
                )
              : undefined
          }
        >
          <Checkbox
            checked={questionnaireWizardPageUiStore.questionnaire.mediaConsentDisclaimer ?? false}
            onChange={(_, data) => {
              questionnaireWizardPageUiStore.setQuestionnaireField({
                key: 'mediaConsentDisclaimer',
                value: data.checked === true,
              });
            }}
            label={{
              children: (
                <Text>
                  <Text weight='semibold' block>Photograph/Video Consent</Text>
                  I affirm that the individuals in these photographs/videos consented to having their image shared with Microsoft for the purpose of being featured in the award winner recognition segment of Microsoft Ignite.
                </Text>
              ),
            }}
          />
        </Field>
      </FlexBox>
    </>
  );
}

export const UploadMediaSection = observer(UploadMediaSectionImpl);
